import type { ProductProps } from 'Components/widgets/product/Product.utils';
import { API_AVAILABLE } from 'Utils/api-status/constants';

function sortProductsByPriceAsc({ price: priceA }: ProductProps, { price: priceB }: ProductProps) {
  // equal items sort equally
  if (priceA?.bestPrice?.value === priceB?.bestPrice?.value) {
    return 0;
  }

  // nulls sort after anything else
  if (!priceA?.bestPrice) {
    return 1;
  }

  if (!priceB?.bestPrice) {
    return -1;
  }

  return priceA?.bestPrice.value < priceB?.bestPrice.value ? -1 : 1;
}

function sortProductsByPriceDesc({ price: priceA }: ProductProps, { price: priceB }: ProductProps) {
  // equal items sort equally
  if (priceA?.bestPrice?.value === priceB?.bestPrice?.value) {
    return 0;
  }

  // nulls sort after anything else
  if (!priceA?.bestPrice) {
    return 1;
  }

  if (!priceB?.bestPrice) {
    return -1;
  }

  return priceA?.bestPrice.value < priceB?.bestPrice.value ? 1 : -1;
}

function sortProductsByPrice(
  products: ReadonlyArray<ProductProps>,
  orderByPrice: 'asc' | 'desc',
): Array<ProductProps> {
  const sortFunction = orderByPrice === 'asc' ? sortProductsByPriceAsc : sortProductsByPriceDesc;
  return [...products].sort(sortFunction);
}

function sortProductsByChildcareAvailabilityDesc(productA: ProductProps, productB: ProductProps) {
  let numberOfChildcaresWithBestAvailabilityA = 0;
  const childcareItemsA = productA?.childcare?.items || [];

  if (childcareItemsA.length > 0) {
    childcareItemsA.forEach(({ availability, isIncluded, remainingStock }) => {
      if (remainingStock === null) {
        if (isIncluded && availability === API_AVAILABLE) {
          numberOfChildcaresWithBestAvailabilityA++;
        }
      } else if (remainingStock >= 10) {
        numberOfChildcaresWithBestAvailabilityA++;
      }
    });
  }

  let numberOfChildcaresWithBestAvailabilityB = 0;
  const childcareItemsB = productB?.childcare?.items || [];

  if (childcareItemsB.length > 0) {
    childcareItemsB.forEach(({ availability, isIncluded, remainingStock }) => {
      if (remainingStock === null) {
        if (isIncluded && availability === API_AVAILABLE) {
          numberOfChildcaresWithBestAvailabilityB++;
        }
      } else if (remainingStock >= 10) {
        numberOfChildcaresWithBestAvailabilityB++;
      }
    });
  }

  if (numberOfChildcaresWithBestAvailabilityA === numberOfChildcaresWithBestAvailabilityB) {
    return 0;
  }

  return numberOfChildcaresWithBestAvailabilityA < numberOfChildcaresWithBestAvailabilityB ? 1 : -1;
}

function sortProductsByChildcareAvailability(
  products: ReadonlyArray<ProductProps>,
  orderByPrice?: 'asc' | 'desc',
): Array<ProductProps> {
  const productsWithBestChildcareAvailability = [];
  const productsWithChildcareAvailability = [];
  const productsWithMissingChildcareAvailability = [];
  products.forEach((product) => {
    let remainingStockGreaterOrEqualTo10Count = 0;
    let unavailableCount = 0;
    const childcareItems = product?.childcare?.items || [];

    if (childcareItems.length > 0) {
      childcareItems.forEach(({ availability, isIncluded, remainingStock }) => {
        if (remainingStock === null) {
          if (isIncluded && availability === API_AVAILABLE) {
            remainingStockGreaterOrEqualTo10Count++;
          } else {
            unavailableCount++;
          }
        } else if (remainingStock >= 10) {
          remainingStockGreaterOrEqualTo10Count++;
        } else if (remainingStock === 0) {
          unavailableCount++;
        }
      });
    } else {
      unavailableCount++;
    }

    if (unavailableCount > 0) {
      productsWithMissingChildcareAvailability.push(product);
    } else if (remainingStockGreaterOrEqualTo10Count === childcareItems.length) {
      productsWithBestChildcareAvailability.push(product);
    } else {
      productsWithChildcareAvailability.push(product);
    }
  });
  return orderByPrice
    ? [
        ...sortProductsByPrice(productsWithBestChildcareAvailability, orderByPrice),
        ...sortProductsByPrice(productsWithChildcareAvailability, orderByPrice),
        ...sortProductsByPrice(productsWithMissingChildcareAvailability, orderByPrice),
      ]
    : [
        ...productsWithBestChildcareAvailability,
        ...productsWithChildcareAvailability.sort(sortProductsByChildcareAvailabilityDesc),
        ...productsWithMissingChildcareAvailability.sort(sortProductsByChildcareAvailabilityDesc),
      ];
}

export {
  sortProductsByChildcareAvailability,
  sortProductsByPrice,
  sortProductsByPriceAsc,
  sortProductsByPriceDesc,
};
